import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, ScrollView, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import PropTypes from 'prop-types';
import {
  clampPillScrollPosition,
  getPillScrollState,
} from '@/constants/utils/pill_scroll';

// Constants
const SCROLL_OFFSET = 200;
const ARROW_INSET = 10;

// Arrow circle size by pill size
const getArrowCircleSize = (size) =>
  size === 'xs' ? 28 : size === 's' ? 32 : 40;

// Create styles outside component
const createStyles = (colors, size) => {
  const circleSize = getArrowCircleSize(size);
  return StyleSheet.create({
    outerContainer: {
      position: 'relative',
      overflow: 'visible',
    },
    container: {
      flexDirection: 'row',
      gap: 4,
    },
    arrow: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: circleSize,
      alignItems: 'center',
      justifyContent: 'center',
    },
    arrowInner: {
      width: circleSize,
      height: circleSize,
      borderRadius: circleSize / 2,
      backgroundColor: colors.solid_color_primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    arrowInnerHover: {
      opacity: 0.9,
    },
    leftArrow: {
      left: ARROW_INSET,
    },
    rightArrow: {
      right: ARROW_INSET,
    },
  });
};

const createPillStyles = (colors, size, hasBackground, isTransparent = false) => StyleSheet.create({
  pillButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: size == 'xs' ? 6 : 8,
    borderWidth: 0,
    minHeight: 28,
    minWidth: 30,
    cursor: 'pointer',
  },
  // Size variants for pills
  pillExtraSmall: {
    height: 22,
    minHeight: 30,
    paddingHorizontal: 8,
  },
  pillSmall: {
    height: 32,
    minHeight: 32,
    paddingHorizontal: 10,
  },
  pillMedium: {
    height: 40,
    minHeight: 40,
    paddingHorizontal: 14,
  },
  // Active/inactive states
  pillActive: {
    backgroundColor: hasBackground ? colors.solid_color_secondary : isTransparent ? 'transparent' : colors.solid_color_secondary,
    textDecorationLine: isTransparent ? 'underline' : 'none',
  },
  pillInactive: {
    backgroundColor: 'transparent',
  },
  pillInactiveHovered: {
    backgroundColor: hasBackground ? colors.solid_color_primary : isTransparent ? 'transparent' : colors.solid_color_secondary,
  },
});

// Memoized pill component
const Pill = React.memo(({ 
  item, 
  isActive, 
  onChange,
  size = 'n',
  hasBackground = false,
  isTransparent = false,
}) => {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const styles = useMemo(() => createPillStyles(colors, size, hasBackground, isTransparent), [colors, size, hasBackground, isTransparent]);

  // Get size-specific styles
  const getSizeStyles = () => {
    switch (size) {
      case 'xs': return styles.pillExtraSmall;
      case 's': return styles.pillSmall;
      default: return styles.pillMedium;
    }
  };

  // Get icon size based on pill size
  const getIconSize = () => {
    switch (size) {
      case 'xs': return 12;
      case 's': return 14;
      default: return 16;
    }
  };

  // Get text size based on pill size
  const getTextSize = () => {
    switch (size) {
      case 'xs': return 'xs';
      case 's': return 's';
      default: return 'n';
    }
  };

  // Get text color based on active state
  const getTextColor = () => {
    if (isActive) {
      return colors.text_color_primary;
    }
    return colors.text_color_primary;
  };

  return (
    <Pressable
      style={[
        styles.pillButton,
        getSizeStyles(),
        isActive ? styles.pillActive : styles.pillInactive,
        !isActive && isHovered && styles.pillInactiveHovered,
      ]}
      onPress={() => onChange?.(item.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={item.label}
      accessibilityState={{ selected: isActive }}
    >
      {item.icon && (
        <Icon
          name={item.icon}
          size={getIconSize()}
          color={getTextColor()}
        />
      )}
      <Text
        size={getTextSize()}
        color={getTextColor()}
        numberOfLines={1}
        weight={isActive && hasBackground ? 'medium' : 'normal'}
      >
        {item.label}
      </Text>
    </Pressable>
  );
});

// Memoized arrow button component
const ArrowButton = React.memo(({ 
  direction, 
  onPress,
  styles,
  colors,
  size = 'n',
}) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Tooltip label={direction === 'left' ? 'Previous' : 'Next'}>
      <Pressable
        onPress={onPress}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onHoverIn={() => setIsHovering(true)}
        onHoverOut={() => setIsHovering(false)}
        style={() => [
          styles.arrow,
          direction === 'left' ? styles.leftArrow : styles.rightArrow,
        ]}  
      >
        <View style={[
          styles.arrowInner,
          isHovering && styles.arrowInnerHover,
        ]}>
          <Icon
            name={`chevron-${direction}`}
            size={size === 'xs' ? 12 : size === 's' ? 14 : 16}
            color={colors.text_color_primary}
          />
        </View>
      </Pressable>
    </Tooltip>
  );
});

function Pills({
  items,
  activeId,
  onChange,
  containerStyle,
  size = 'n',
  hasBackground = false,
  isTransparent = false,
  hideArrows = false,
  scrollToActiveId = false,
}) {
  const { colors } = useTheme();
  const scrollRef = useRef(null);
  const pillLayoutsRef = useRef({});
  const [scrollPosition, setScrollPosition] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [layoutWidth, setLayoutWidth] = useState(0);

  const {
    maxScrollPosition,
    showLeftArrow,
    showRightArrow,
  } = getPillScrollState({
    scrollPosition,
    layoutWidth,
    contentWidth,
    hideArrows,
  });

  // Memoize styles
  const styles = useMemo(() => createStyles(colors, size), [colors, size]);

  useEffect(() => {
    if (!scrollToActiveId || !activeId || !scrollRef.current) return;
    const layout = pillLayoutsRef.current[activeId];
    if (!layout || !layoutWidth) return;
    const targetX = clampPillScrollPosition(
      layout.x - layoutWidth / 2 + layout.width / 2,
      layoutWidth,
      contentWidth,
    );
    scrollRef.current.scrollTo({ x: targetX, animated: true });
  }, [activeId, scrollToActiveId, layoutWidth, contentWidth]);

  const handlePillLayout = useCallback((id, event) => {
    const { x, width } = event.nativeEvent.layout;
    pillLayoutsRef.current[id] = { x, width };
  }, []);

  const updateScrollLayout = useCallback((scrollX, layoutW, contentW) => {
    setScrollPosition(clampPillScrollPosition(scrollX, layoutW, contentW));
    setLayoutWidth(layoutW);
    setContentWidth(contentW);
  }, []);

  const handleScroll = useCallback((event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    updateScrollLayout(contentOffset.x, layoutMeasurement.width, contentSize.width);
  }, [updateScrollLayout]);

  const handleContentSizeChange = useCallback((contentW) => {
    setContentWidth(contentW);
  }, []);

  const handleLayout = useCallback((event) => {
    const { width } = event.nativeEvent.layout;
    setLayoutWidth(width);
  }, []);

  const scrollLeft = useCallback(() => {
    if (scrollRef.current) {
      const newX = Math.max(0, scrollPosition - SCROLL_OFFSET);
      scrollRef.current.scrollTo({
        x: newX,
        animated: true,
      });
    }
  }, [scrollPosition]);

  const scrollRight = useCallback(() => {
    if (scrollRef.current) {
      const newX = Math.min(maxScrollPosition, scrollPosition + SCROLL_OFFSET);
      scrollRef.current.scrollTo({
        x: newX,
        animated: true,
      });
    }
  }, [maxScrollPosition, scrollPosition]);

  const arrowGutter = getArrowCircleSize(size) + ARROW_INSET + 4;
  const scrollViewStyle = [
    containerStyle,
    showLeftArrow && { marginLeft: arrowGutter },
    showRightArrow && { marginRight: arrowGutter },
  ];

  return (
    <View 
      style={styles.outerContainer}
      accessibilityRole="tablist"
      accessibilityLabel="Navigation pills"
    >
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
        scrollEventThrottle={16}
        style={scrollViewStyle}
        accessibilityRole="none"
      >
        <View style={styles.container}>
          {Array.isArray(items) && items.map((item, index) => (
            <View
              key={`pill-${index}`}
              onLayout={scrollToActiveId ? (e) => handlePillLayout(item.id, e) : undefined}
            >
              <Pill
                item={item}
                isActive={item.id === activeId}
                onChange={onChange}
                size={size}
                hasBackground={hasBackground}
                isTransparent={isTransparent}
              />
            </View>
          ))}
        </View>
      </ScrollView>
      
      {showLeftArrow && (
        <ArrowButton
          direction="left"
          onPress={scrollLeft}
          styles={styles}
          colors={colors}
          size={size}
        />
      )}
      
      {showRightArrow && (
        <ArrowButton
          direction="right"
          onPress={scrollRight}
          styles={styles}
          colors={colors}
          size={size}
        />
      )}
    </View>
  );
}

Pills.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
  })).isRequired,
  size: PropTypes.oneOf(['n', 's', 'xs']),
  activeId: PropTypes.string,
  onChange: PropTypes.func,
  containerStyle: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
  ]),
  hideArrows: PropTypes.bool,
};

Pills.displayName = 'Pills';

export default React.memo(Pills);
